/**
 * ImageCropModal.tsx
 * Modal pour recadrer une image
 */
import React, { useState, useCallback } from 'react';
// P3 : `react-easy-crop/types` n'est plus exposé en v5+ ; types importés depuis le root.
import Cropper, { type Area, type Point } from 'react-easy-crop';
import { cropImage, type CropArea } from '../../../services/ImageCropService';
import { logger } from '../../../utils/ProfessionalLogger';

export interface ImageCropModalProps {
  isOpen: boolean;
  imageSrc: string;
  onClose: () => void;
  onCropComplete: (croppedImageBlob: Blob, cropData: CropArea) => void;
}

export function ImageCropModal({
  isOpen,
  imageSrc,
  onClose,
  onCropComplete,
}: ImageCropModalProps): React.ReactElement | null {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropChange = useCallback((crop: Point) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleApply = useCallback(async () => {
    if (!croppedAreaPixels || !imageSrc) {
      return;
    }

    setIsProcessing(true);
    try {
      // Charger l'image
      const image = new window.Image();
      image.crossOrigin = 'anonymous';
      image.src = imageSrc;

      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
      });

      // Recadrer l'image
      const cropData: CropArea = {
        x: croppedAreaPixels.x,
        y: croppedAreaPixels.y,
        width: croppedAreaPixels.width,
        height: croppedAreaPixels.height,
      };

      const croppedBlob = await cropImage(image, cropData);
      onCropComplete(croppedBlob, cropData);
      onClose();
    } catch (error) {
      logger.error('Erreur lors du recadrage:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [croppedAreaPixels, imageSrc, onCropComplete, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--color-bg-primary)',
          borderRadius: 'var(--radius-lg)',
          padding: '24px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div
          style={{
            position: 'relative',
            width: '600px',
            height: '400px',
            background: '#000',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={undefined}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropCompleteCallback}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <label
              style={{
                fontSize: '14px',
                color: 'var(--color-text-secondary)',
                minWidth: '80px',
              }}
            >
              Zoom:
            </label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={e => setZoom(parseFloat(e.target.value))}
              style={{
                flex: 1,
              }}
            />
            <span
              style={{
                fontSize: '14px',
                color: 'var(--color-text-secondary)',
                minWidth: '40px',
                textAlign: 'right',
              }}
            >
              {zoom.toFixed(1)}x
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              gap: '8px',
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              style={{
                padding: '8px 16px',
                background: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: '14px',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={isProcessing || !croppedAreaPixels}
              style={{
                padding: '8px 16px',
                background: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isProcessing || !croppedAreaPixels ? 'not-allowed' : 'pointer',
                opacity: isProcessing || !croppedAreaPixels ? 0.6 : 1,
              }}
            >
              {isProcessing ? 'Traitement...' : 'Appliquer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
